import React, { useState, useEffect, useMemo } from 'react';
import { useSearchStore } from '../store/searchStore';
import api, { bulkAddLeads, checkLeadsInPipeline } from '../services/api';
import Header from '../components/Header';
import SearchForm from '../components/SearchForm';
import BusinessCard from '../components/BusinessCard';
import LoadingSkeleton from '../components/LoadingSkeleton';
import UpgradePrompt from '../components/Subscription/UpgradePrompt';
import UsageDisplay from '../components/UsageDisplay';
import BackToDashboard from '../components/BackToDashboard';
import { useUsage } from '../hooks/useUsage';
import { useAuthStore } from '../store/authStore';

interface Business {
  id: number;
  name: string;
  address: string | null;
  rating: number | null;
  has_website: boolean;
  website: string | null;
  review_count?: number;
}

interface LocalSEOResult {
  business_id: number;
  business_name?: string;
  overall_score?: number;
  opportunity_score?: number;
  scores?: {
    gbp?: number;
    website?: number;
    reviews?: number;
    citations?: number;
    relevance?: number;
  };
  evidence?: {
    gbp?: any;
    website?: any;
    reviews?: any;
    citations?: any;
    relevance?: any;
  };
  opportunities?: Array<{
    priority?: string;
    category?: string;
    title?: string;
    description?: string;
    impact?: string;
  }>;
  issues?: (string | { field?: string; google?: string; website?: string; [key: string]: any })[];
  recommendations?: (string | { field?: string; google?: string; website?: string; [key: string]: any })[];
  summary?: string;
  error?: string;
}

const Search: React.FC = () => {
  const { 
    results: storedResults, 
    query: storedQuery, 
    location: storedLocation,
    filters: storedFilters,
    hasSearched: storedHasSearched,
    setResults, 
    setSearchParams, 
    setHasSearched 
  } = useSearchStore();
  const { usage, refetch: refetchUsage } = useUsage();
  const { user } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [localSEOLoading, setLocalSEOLoading] = useState(false);
  const [localSEOResults, setLocalSEOResults] = useState<LocalSEOResult[]>([]);
  const [showLocalSEOResults, setShowLocalSEOResults] = useState(false);
  const [onlyPotential, setOnlyPotential] = useState(false);
  const [selectedBusinesses, setSelectedBusinesses] = useState<Set<number>>(new Set());
  const [businessesInPipeline, setBusinessesInPipeline] = useState<Set<number>>(new Set());
  const [bulkAdding, setBulkAdding] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(9);
  const RESULTS_PER_PAGE = 9;
  const MAX_RESULTS = usage?.max_results_per_search ?? 20;

  // Check which businesses are in pipeline when results change
  useEffect(() => {
    if (storedResults.length > 0) {
      const businessIds = storedResults.map(b => b.id);
      checkLeadsInPipeline(businessIds)
        .then((result) => {
          const inPipeline = new Set<number>();
          Object.entries(result).forEach(([id, isInPipeline]) => {
            if (isInPipeline) {
              inPipeline.add(parseInt(id));
            }
          });
          setBusinessesInPipeline(inPipeline);
        })
        .catch((err) => {
          console.error('Error checking pipeline status:', err);
        });
    }
  }, [storedResults]);

  const handleSearch = async (query: string, location: string, filters: any) => {
    if (!query || !location) {
      setError('Please enter both query and location');
      return;
    }

    setLoading(true);
    setError('');
    setLocalSEOResults([]);
    setShowLocalSEOResults(false);
    setHasSearched(true);
    setSearchParams(query, location, filters);
    setDisplayedCount(9); // Reset to initial count on new search

    try {
      const response = await api.post('/search', {
        query,
        location,
        filters,
      });
      const businesses = response.data.businesses || [];
      setResults(businesses);
      refetchUsage(); // Refresh usage count so UI updates immediately
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Error searching businesses';
      setError(errorMessage);
      
      if (err.response?.status === 403 && errorMessage.includes('Upgrade')) {
        setUpgradeMessage(errorMessage);
        setShowUpgrade(true);
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeLocalSEO = async () => {
    if (storedResults.length < 2) {
      setError('Need at least 2 results to analyze Local SEO');
      return;
    }

    setLocalSEOLoading(true);
    setError('');

    try {
      const businessIds = storedResults.slice(0, 2).map(b => b.id);
      const response = await api.post('/search/analyze-local-seo', {
        business_ids: businessIds,
        search_query: storedQuery,
        search_location: storedLocation
      });
      setLocalSEOResults(response.data.results || []);
      setShowLocalSEOResults(true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Error analyzing Local SEO';
      setError(errorMessage);
      if (err?.response?.status === 403) {
        setUpgradeMessage(errorMessage);
        setShowUpgrade(true);
      }
    } finally {
      setLocalSEOLoading(false);
    }
  };

  // Calculate opportunity score for a business
  const calculateOpportunityScore = (business: Business): number => {
    if (!business.has_website) return 95;
    if (business.rating !== null && business.rating < 3.5) return 80;
    if (!business.website) return 75;
    return 50;
  };

  const filteredResults = useMemo(() => {
    let filtered = storedResults;
    const filters = storedFilters || {};

    // Apply basic filters
    if (filters.no_website_only) {
      filtered = filtered.filter(b => !b.has_website);
    }

    if (filters.has_social_media !== undefined) {
      // Note: This filter would need social media data from backend
      // For now, we'll skip this as it requires backend data
    }

    // Apply rating range filter
    const minRating = filters.minRating;
    if (minRating !== undefined) {
      filtered = filtered.filter(b => b.rating !== null && b.rating >= minRating);
    }
    const maxRating = filters.maxRating;
    if (maxRating !== undefined) {
      filtered = filtered.filter(b => b.rating === null || b.rating <= maxRating);
    }

    // Apply review count range filter
    const minReviewCount = filters.minReviewCount;
    if (minReviewCount !== undefined) {
      filtered = filtered.filter(b => (b.review_count || 0) >= minReviewCount);
    }
    const maxReviewCount = filters.maxReviewCount;
    if (maxReviewCount !== undefined) {
      filtered = filtered.filter(b => (b.review_count || 0) <= maxReviewCount);
    }

    // Apply opportunity score range filter
    const minOpportunityScore = filters.minOpportunityScore;
    const maxOpportunityScore = filters.maxOpportunityScore;
    if (minOpportunityScore !== undefined || maxOpportunityScore !== undefined) {
      filtered = filtered.filter(b => {
        const score = calculateOpportunityScore(b);
        if (minOpportunityScore !== undefined && score < minOpportunityScore) return false;
        if (maxOpportunityScore !== undefined && score > maxOpportunityScore) return false;
        return true;
      });
    }

    // Apply distance filter (would need location data - skip for now as it requires coordinates)
    // if (filters.maxDistance !== undefined) {
    //   // Would need to calculate distance from search location
    // }

    // Apply "only potential" toggle
    if (onlyPotential) {
      filtered = filtered.filter(b => !b.has_website || (b.rating !== null && b.rating < 3.5));
    }

    return filtered;
  }, [storedResults, storedFilters, onlyPotential]);

  const handleLoadMore = () => {
    const nextCount = Math.min(
      displayedCount + RESULTS_PER_PAGE,
      MAX_RESULTS,
      filteredResults.length
    );
    setDisplayedCount(nextCount);
  };

  const handleSelectBusiness = (businessId: number, selected: boolean) => {
    setSelectedBusinesses(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(businessId);
      } else {
        newSet.delete(businessId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const availableBusinesses = filteredResults.filter(b => !businessesInPipeline.has(b.id));
    if (selectedBusinesses.size === availableBusinesses.length) {
      // Deselect all
      setSelectedBusinesses(new Set());
    } else {
      // Select all available
      setSelectedBusinesses(new Set(availableBusinesses.map(b => b.id)));
    }
  };

  const handleBulkAdd = async () => {
    if (selectedBusinesses.size === 0) return;

    setBulkAdding(true);
    try {
      const businessIds = Array.from(selectedBusinesses);
      const result = await bulkAddLeads(businessIds);
      
      // Update pipeline status for successfully added businesses
      const newInPipeline = new Set(businessesInPipeline);
      result.success.forEach((id: number) => {
        newInPipeline.add(id);
      });
      setBusinessesInPipeline(newInPipeline);
      
      // Remove successfully added from selection
      setSelectedBusinesses(prev => {
        const newSet = new Set(prev);
        result.success.forEach((id: number) => newSet.delete(id));
        return newSet;
      });

      // Show success message
      const successCount = result.success.length;
      const skippedCount = result.skipped?.length || 0;
      const failedCount = result.failed?.length || 0;
      
      let message = `Successfully added ${successCount} business${successCount !== 1 ? 'es' : ''} to pipeline.`;
      if (skippedCount > 0) {
        message += ` ${skippedCount} already in pipeline.`;
      }
      if (failedCount > 0) {
        message += ` ${failedCount} failed to add.`;
      }
      alert(message);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Error adding businesses to pipeline';
      alert(errorMessage);
      if (err.response?.status === 403 && errorMessage.includes('Upgrade')) {
        setUpgradeMessage(errorMessage);
        setShowUpgrade(true);
      }
    } finally {
      setBulkAdding(false);
    }
  };

  const handleExportSelectedPDF = async () => {
    if (selectedBusinesses.size === 0) return;

    setExportingPDF(true);
    try {
      const businessIds = Array.from(selectedBusinesses);
      const response = await api.post('/export/businesses/pdf', {
        business_ids: businessIds
      }, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `businesses_export_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error exporting PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header activeTab="intelligence" />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-12">
        <BackToDashboard />
        <UsageDisplay />
        <SearchForm 
          onSearch={handleSearch} 
          isLoading={loading}
          isSearchBlocked={usage?.is_search_blocked ?? false}
          initialQuery={storedQuery}
          initialLocation={storedLocation}
          initialFilters={storedFilters}
          searchCountry={user?.search_country ?? undefined}
          isFreeTier={usage?.tier === 'free'}
          searchTier={usage?.tier}
          resultCount={storedHasSearched ? filteredResults.length : undefined}
          maxResultsPerSearch={usage?.max_results_per_search ?? undefined}
        />

        {!storedHasSearched && (
          <div className="mb-20 animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <div className="inline-block px-4 py-1.5 bg-indigo-50 border border-indigo-100 rounded-full text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-6">
                Consultant Operating System
              </div>
              <h2 className="text-5xl lg:text-7xl font-black text-slate-900 tracking-tightest leading-[1] mb-8">
                Scan. Analyze. Score. <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-emerald-500">
                  Reach Out & Close.
                </span>
              </h2>
              <p className="text-xl text-slate-500 font-medium max-w-2xl mx-auto leading-relaxed">
                The high-performance platform for IT consultants. Identify local tech debt, generate automated audits, and pitch with verified intelligence.
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-8">
          {storedHasSearched && (
            <>
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white/50 p-4 rounded-xl border border-slate-200 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-600 font-medium">
                    Scan results: <span className="text-indigo-600 font-black">{filteredResults.length}</span> businesses analyzed
                  </span>
                  {selectedBusinesses.size > 0 && (
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-black">
                      {selectedBusinesses.size} selected
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-6">
                  {storedResults.length >= 2 && (
                    <button
                      onClick={handleAnalyzeLocalSEO}
                      disabled={localSEOLoading}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-emerald-700 transition-all disabled:bg-slate-300"
                    >
                      {localSEOLoading ? 'Analyzing...' : 'Analyze Local SEO (First 2)'}
                    </button>
                  )}
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative inline-flex items-center">
                    <input 
                      type="checkbox" 
                      checked={onlyPotential} 
                      onChange={(e) => setOnlyPotential(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-all duration-300 ease-in-out shadow-inner ${
                      onlyPotential ? 'bg-indigo-600' : 'bg-slate-300'
                    }`}>
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ease-in-out ${
                        onlyPotential ? 'translate-x-5' : 'translate-x-0'
                      }`}></div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-slate-700 group-hover:text-indigo-600 transition-colors">
                    Show only high potential
                  </span>
                </label>
                </div>
              </div>
              
              {filteredResults.length > 0 && (
                <div className="flex items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleSelectAll}
                      className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
                    >
                      {selectedBusinesses.size === filteredResults.filter(b => !businessesInPipeline.has(b.id)).length 
                        ? 'Deselect All' 
                        : 'Select All'}
                    </button>
                    <span className="text-sm text-slate-600">
                      {filteredResults.filter(b => !businessesInPipeline.has(b.id)).length} available to add
                    </span>
                  </div>
                  {selectedBusinesses.size > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleExportSelectedPDF}
                        disabled={exportingPDF}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-emerald-700 transition-all disabled:bg-slate-300 flex items-center gap-2"
                      >
                        {exportingPDF ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                            Exporting...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Export PDF
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleBulkAdd}
                        disabled={bulkAdding}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-all disabled:bg-slate-300"
                      >
                        {bulkAdding ? 'Adding...' : `Add ${selectedBusinesses.size} to Pipeline`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <section>
            {error && (
              <div className="p-6 bg-rose-50 text-rose-700 rounded-3xl border border-rose-100 mb-10 flex items-center gap-4">
                <div className="w-10 h-10 bg-rose-200 rounded-xl flex items-center justify-center shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-black text-sm uppercase tracking-widest">Error</h4>
                  <p className="text-xs font-semibold opacity-80">{error}</p>
                </div>
              </div>
            )}

            {loading && <LoadingSkeleton count={3} />}

            {!loading && filteredResults.length > 0 ? (
              <>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {filteredResults.slice(0, displayedCount).map((business) => (
                    <BusinessCard 
                      key={business.id} 
                      business={business}
                      isSelected={selectedBusinesses.has(business.id)}
                      isInPipeline={businessesInPipeline.has(business.id)}
                      onSelect={handleSelectBusiness}
                      showCheckbox={true}
                    />
                  ))}
                </div>
                
                {/* Load More Section */}
                {displayedCount < Math.min(MAX_RESULTS, filteredResults.length) && (
                  <div className="flex flex-col items-center gap-4 mt-8">
                    <div className="text-sm text-slate-600 font-medium">
                      Showing {displayedCount} of {Math.min(MAX_RESULTS, filteredResults.length)} results
                    </div>
                    <button
                      onClick={handleLoadMore}
                      className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold transition-all shadow-sm active:scale-95 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                      Load More ({Math.min(MAX_RESULTS, filteredResults.length) - displayedCount} remaining)
                    </button>
                  </div>
                )}
                
                {displayedCount >= Math.min(MAX_RESULTS, filteredResults.length) && filteredResults.length > 0 && (
                  <div className="text-center mt-8 py-4 text-sm text-slate-600 font-medium">
                    Showing all {Math.min(MAX_RESULTS, filteredResults.length)} results
                  </div>
                )}
              </>
            ) : !loading && storedHasSearched && (
              <div className="h-[50vh] flex flex-col items-center justify-center text-center p-12 bg-white rounded-[3rem] border border-slate-200 border-dashed">
                <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-8 border border-slate-100">
                  <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-3">No High Potential Matches</h3>
                <p className="text-slate-500 max-w-sm font-medium leading-relaxed">
                  {onlyPotential 
                    ? 'Try disabling the "High Potential" toggle to view all analyzed businesses.' 
                    : 'Try adjusting search parameters or widening your scan radius.'}
                </p>
              </div>
            )}

            {showLocalSEOResults && localSEOResults.length > 0 && (
              <div className="mt-10 space-y-6">
                <h3 className="text-2xl font-black text-slate-900">Local SEO Analysis Results</h3>
                {localSEOResults.map((result) => (
                  <div key={result.business_id} className="bg-white rounded-2xl border border-slate-200 p-6">
                    <h4 className="text-xl font-black text-slate-900 mb-4">
                      {result.business_name || `Business ID: ${result.business_id}`}
                    </h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-sm font-bold text-slate-600 mb-1">Local SEO Score</div>
                        <div className="text-3xl font-black text-indigo-600">
                          {result.overall_score?.toFixed(1)}/100
                        </div>
                      </div>
                      {result.opportunity_score !== undefined && (
                        <div>
                          <div className="text-sm font-bold text-slate-600 mb-1">Opportunity Score</div>
                          <div className="text-3xl font-black text-emerald-600">
                            {result.opportunity_score.toFixed(1)}/100
                          </div>
                        </div>
                      )}
                    </div>
                    {result.summary && (
                      <p className="text-sm text-slate-600 mb-4">{result.summary}</p>
                    )}
                    {result.opportunities && result.opportunities.length > 0 && (
                      <div className="mt-4 p-4 bg-indigo-50 rounded-xl">
                        <h5 className="text-sm font-black text-indigo-600 uppercase mb-2">Top Opportunities</h5>
                        <ul className="space-y-2">
                          {result.opportunities.slice(0, 5).map((opp, idx) => (
                            <li key={idx} className="text-xs text-slate-700">
                              <strong>{opp.title}</strong>: {opp.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      <UpgradePrompt
        message={upgradeMessage}
        show={showUpgrade}
        onClose={() => setShowUpgrade(false)}
      />
    </div>
  );
};

export default Search;
